export interface ChatToolCall {
  tool: string;
  is_error: boolean;
  summary?: string | null;
  arguments?: Record<string, unknown> | null;
}

export interface ChatDatasetArtifact {
  dataset_id?: string | null;
  tool: string;
  meta?: Record<string, unknown> | null;
  preview_rows: Record<string, unknown>[];
  /** All district rows used to colour the map. The table preview stays capped. */
  map_rows?: Record<string, unknown>[];
  columns: string[];
}

export interface ChatChartArtifact {
  tool: string;
  chart_spec: {
    chart_type?: string;
    title?: string;
    x?: string;
    y?: string;
    series?: string;
    points?: Array<Record<string, unknown>>;
  };
}

export interface ChatMapArtifact {
  tool: string;
  kind: 'districts' | 'features' | 'wms' | string;
  geo_id?: string | null;
  districts?: Array<Record<string, unknown>> | null;
  features?: Array<Record<string, unknown>> | null;
  wms?: Record<string, unknown> | null;
  warnings?: string[];
}

export interface ChatJobArtifact {
  tool: string;
  job_id: string;
  status?: string | null;
  progress_pct?: number | null;
  template_id?: string | null;
  format?: string | null;
  download_url?: string | null;
  error?: string | null;
  file_name?: string | null;
  report_id?: string | null;
}

export interface ChatFileArtifact {
  tool: string;
  report_id?: string | null;
  download_url?: string | null;
  file_name?: string | null;
  format?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
}

export interface ChatErrorArtifact {
  tool: string;
  code?: string | null;
  user_message_ru?: string | null;
  user_message_uz?: string | null;
  message?: string | null;
}

export interface ChatTurnArtifacts {
  datasets: ChatDatasetArtifact[];
  charts: ChatChartArtifact[];
  maps?: ChatMapArtifact[];
  jobs: ChatJobArtifact[];
  files: ChatFileArtifact[];
  errors: ChatErrorArtifact[];
}

export interface ChatSessionContext {
  user_id: string;
  role?: string | null;
  allowed_geo_ids: string[];
  locale?: string | null;
  max_tool_rounds: number;
}

export interface ChatHttpResponse {
  session_id: string;
  correlation_id: string;
  reply: string;
  rounds: number;
  tool_calls: ChatToolCall[];
  artifacts?: ChatTurnArtifacts;
  session_context?: ChatSessionContext | null;
  error?: string | null;
  status?: string | null;
  error_code?: string | null;
  filter_conflict?: boolean;
  context?: Record<string, unknown> | unknown[] | null;
  keyboard?: unknown;
}

export interface GuideButton {
  id: string;
  label: string;
  action: 'callback' | 'ask' | 'link' | string;
  icon?: string | null;
  question?: string | null;
  url?: string | null;
}

export interface GuideMessage {
  id?: string | null;
  role?: string | null;
  content: string;
  kind?: string | null;
  keyboard?: unknown;
}

export interface GuideHttpResponse {
  session_id?: string | null;
  replace?: boolean;
  messages?: GuideMessage[] | null;
}

/** `/reports` and `/reports/generator` open the constructor on this page. */
export function isConstructorGuideLink(url: string | null | undefined): boolean {
  if (!url || /^https?:/i.test(url)) {
    return false;
  }
  const path = url.split('?')[0].replace(/\/+$/, '').replace(/^\//, '').toLowerCase();
  return path === 'reports' || path === 'reports/generator';
}

export function guideButtonIcon(icon: string | null | undefined): string | null {
  switch (icon) {
    case 'question':
      return 'pi-question-circle';
    case 'back':
      return 'pi-arrow-left';
    case 'next':
      return 'pi-arrow-right';
    case 'confirm':
      return 'pi-check';
    case 'cancel':
      return 'pi-times';
    case 'return':
      return 'pi-replay';
    case 'link':
      return 'pi-external-link';
    default:
      return null;
  }
}

/** Telegram-style rows. A flat list puts each example and link on its own row and navigation last. */
export function keyboardRows(keyboard: unknown): GuideButton[][] {
  const source = unwrapKeyboard(keyboard);
  if (!Array.isArray(source) || source.length === 0) {
    return [];
  }
  if (Array.isArray(source[0])) {
    return source
      .map((row) => (Array.isArray(row) ? row.map(parseGuideButton).filter((button): button is GuideButton => !!button) : []))
      .filter((row) => row.length > 0);
  }
  const buttons = source.map(parseGuideButton).filter((button): button is GuideButton => !!button);
  const navigation = buttons.filter((button) => button.action === 'callback');
  const rest = buttons.filter((button) => button.action !== 'callback');
  const rows = rest.map((button) => [button]);
  if (navigation.length) {
    rows.push(navigation);
  }
  return rows;
}

function unwrapKeyboard(keyboard: unknown): unknown {
  if (!keyboard || typeof keyboard !== 'object' || Array.isArray(keyboard)) {
    return keyboard;
  }
  const record = keyboard as Record<string, unknown>;
  return record['rows'] ?? record['inline_keyboard'] ?? record['buttons'] ?? keyboard;
}

function parseGuideButton(value: unknown): GuideButton | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const label = typeof record['label'] === 'string' ? record['label'] : '';
  const id = typeof record['id'] === 'string' ? record['id'] : label;
  if (!label && !id) {
    return null;
  }
  return {
    id,
    label: label || id,
    action: typeof record['action'] === 'string' ? record['action'] : 'callback',
    icon: typeof record['icon'] === 'string' ? record['icon'] : null,
    question: typeof record['question'] === 'string' ? record['question'] : null,
    url: typeof record['url'] === 'string' ? record['url'] : null
  };
}

export interface DataConstraints {
  weather_available_from?: string;
  crop_available_from?: string;
  vegetation_index_available_from?: string;
  sentinel_cadence_days?: number;
  timezone?: string;
  max_rows_per_query?: number;
  unavailable_domains?: string[];
}

export function newAnalyticsId(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface ChatUserAttachment {
  file_id: string;
  file_name?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  download_url?: string | null;
}

export interface ChatAttachmentUpload {
  file_id: string;
  file_name?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
}

export interface AnalyticsChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  time: Date;
  attachments?: ChatUserAttachment[];
  artifacts?: ChatTurnArtifacts;
  tool_calls?: ChatToolCall[];
  rounds?: number;
  session_id?: string;
  correlation_id?: string;
  error?: string | null;
  kind?: string | null;
  keyboard?: GuideButton[][] | null;
  assistantMessageId?: string | null;
}

export interface StoredChatSession {
  id: string;
  title: string;
  updatedAt: string;
  starred: boolean;
}

export const emptyArtifacts = (): ChatTurnArtifacts => ({
  datasets: [],
  charts: [],
  maps: [],
  jobs: [],
  files: [],
  errors: []
});
